import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDashboardData } from '../../hooks/useDashboardData';
import { useFilters } from '../../contexts/FilterContext';
import { buildTerritoryRows } from '../../utils/territoryRows';
import { buildMetricKpis } from '../../utils/kpis';
import { isBelowStatus } from '../../utils/status';
import type { TrendMetric } from '../../utils/metricConfig';
import { KpiCard } from '../ui/KpiCard';
import { EmptyState } from '../ui/EmptyState';
import { TrendPanel } from '../charts/TrendPanel';
import { MonthlyTable } from '../overview/MonthlyTable';
import { RankingTable } from '../overview/RankingTable';
import { MonthDrawer } from '../overview/MonthDrawer';
export function MetricDashboard({
  metric
}: {
  metric: TrendMetric;
}) {
  const data = useDashboardData();
  const {
    reset
  } = useFilters();
  const navigate = useNavigate();
  const {
    search
  } = useLocation();
  const [drawerMonth, setDrawerMonth] = useState<number | null>(null);
  const closeDrawer = useCallback(() => setDrawerMonth(null), []);
  const rows = useMemo(() => buildTerritoryRows(data.grouped, metric, data.range, data.lyRange, data.network.all, data.networkUnits), [data, metric]);
  const kpis = useMemo(() => buildMetricKpis({
    metric,
    series: data.series,
    network: data.network,
    range: data.range,
    lyRange: data.lyRange,
    scopeUnits: data.scopeUnits,
    networkUnits: data.networkUnits,
    unitLabel: 'territory',
    single: data.singleTerritory,
    scopeLabel: data.scopeLabel,
    below: {
      count: rows.filter(r => isBelowStatus(r.status)).length,
      total: rows.length,
      noun: 'Territories'
    }
  }), [data, metric, rows]);
  const openTerritory = (id: string) => {
    setDrawerMonth(null);
    navigate(`/territory/${id}${search}`);
  };
  if (data.territoryIds.length === 0) {
    return <div className="rounded-lg border border-line bg-white" data-id="element-63" data-mp="22f42y93tsge6">
        <EmptyState title="No territories match these filters" description="The selected territories are not assigned to the chosen FBC / AD." action={<button type="button" onClick={reset} className="text-xs font-semibold text-navy hover:underline" data-id="element-65" data-mp="2cc0bufxmz4j9">Reset filters</button>} data-id="element-64" data-mp="22goxhyq53zc1" />
      </div>;
  }
  return <div className="space-y-5" data-id="element-66" data-mp="2l5v7mxcvbim8">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5" data-id="element-67" data-mp="2l4k5t32zgkxr">
        {kpis.map(k => <KpiCard key={k.id} item={k} data-id="element-68" data-mp="2q2vgfg13qzh1" />)}
      </div>

      <TrendPanel metric={metric} series={data.series} network={data.network} range={data.range} scopeUnits={data.scopeUnits} networkUnits={data.networkUnits} unitLabel="territory" entityLabel={data.scopeLabel} showScopeAvg={!data.isNetworkScope} onMonthClick={setDrawerMonth} data-id="element-69" data-mp="2v2zu6yamlg3k" />

      <MonthlyTable metric={metric} series={data.series} network={data.network} range={data.range} scopeUnits={data.scopeUnits} networkUnits={data.networkUnits} onMonthClick={setDrawerMonth} data-id="element-70" data-mp="15qfy6eh4epmp" />

      <RankingTable rows={rows} metric={metric} range={data.range} onOpen={openTerritory} data-id="element-71" data-mp="3a7cfzwgd4ypk" />

      <MonthDrawer metric={metric} month={drawerMonth} onClose={closeDrawer} series={data.series} network={data.network} networkUnits={data.networkUnits} scopeUnits={data.scopeUnits} rows={rows} onOpen={openTerritory} data-id="element-72" data-mp="358j0xzlfgzna" />
    </div>;
}